<template lang="pug">
#follower-list(v-cloak)
  .box
    .box-header
      .d-flex.justify-content-start.align-items-center
        .d-flex.justify-content-start.flex-column
          .title
            span フォロワー
          .d-flex.flex-row
            span 登録人数：
            b-spinner.spinner.ml-2(v-if="followerCont == null")
            span.follower-count(v-else) {{ followerCont }}人
    .box-body
      b-tabs(v-model="tabIndex")
        // send messages
        b-tab.tab
          template(v-slot:title)
            .tab-title
              span 新規送信
          SendMessagesTab(ref="sendMessagesTab" @sendMessage="sendMessage")
        // message history
        b-tab.tab
          template(v-slot:title)
            .tab-title
              span メッセージ履歴
          SendMessageHistoryTab(ref="sendMessageHistoryTab")

  OkModal(v-model="showSendMessageSuccessModal" title="メッセージ送信を受け付けました。" @ok="onClose")

  //- file upload error modal
  FileUploadErrorModal(v-model="showUploadErrorModal")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
  ProgressLoading(v-model="currentUploadCount" :max="uploadCount" :isBusy="progressLoading" :message="progressMessage")
</template>

<script lang="ts">
import { Component, Inject, Ref, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore } from "@/store/store";
import { PcUrl } from "@/commons/constants/PcUrl";

import FollowersService, { FollowersGetModel } from "@/commons/services/Followers";
import FollowerMessagesService, { FollowerMessagesPostModel, File as UploadFile } from "@/commons/services/FollowerMessages";
import S3LocationsService from "@/commons/services/S3Locations";
import { S3LocationRequest, S3LocationResponse, S3LocationType, S3LocationMessage } from "@/commons/models/S3Location";
import S3UploadsService from "@/commons/services/S3Uploads";
import ExistS3FilesService, { ExistS3FilesPostModel } from "@/commons/services/ExistS3Files";

import FileUploadErrorModal from "@/pc/basics/FileUploadErrorModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import SendMessagesTab, { SendMessageModel } from "@/pc/containers/followers/SendMessagesTab.vue";
import SendMessageHistoryTab from "@/pc/containers/followers/SendMessageHistoryTab.vue";
import ProgressLoading from "@/pc/basics/ProgressLoading.vue";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

/**
 * ### フォロワー一覧画面コンポーネント
 */
@Component({
  components: {
    FileUploadErrorModal,
    SendMessagesTab,
    SendMessageHistoryTab,
    PageLoading,
    ProgressLoading,
    OkModal
  }
})
export default class FollowerList extends Vue {
  /** フォロワーサービス */
  @Inject("FollowersService") followersService!: FollowersService;
  /** フォロワーメッセージサービス */
  @Inject("FollowerMessagesService") followerMessagesService!: FollowerMessagesService;
  /** S3格納先URLサービス */
  @Inject("S3LocationsService") s3LocationsService!: S3LocationsService;
  /** S3ファイルアップロードサービス */
  @Inject("S3UploadsService") s3UploadsService!: S3UploadsService;
  /** S3ファイル存在確認サービス */
  @Inject("ExistS3FilesService") existS3FilesService!: ExistS3FilesService;

  /** メッセージ送信タブ */
  @Ref() sendMessagesTab!: SendMessagesTab;
  /** メッセージ履歴タブ */
  @Ref() sendMessageHistoryTab!: SendMessageHistoryTab;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** メッセージ送信完了モーダル表示可否 */
  showSendMessageSuccessModal: boolean = false;

  /** 現在表示しているタブ番号 */
  tabIndex: number = 0;

  /** フォロワー登録数 */
  followerCont: number | null = null;

  /** 現在のアップロード数 */
  currentUploadCount: number = 0;
  /** アップロード数最大値 */
  uploadCount: number = 0;
  /** プログレス付きローディング表示可否 */
  progressLoading: boolean = false;
  /** プログレス付きローディングメッセージ */
  progressMessage: string = "";
  /** ファイルアップロードエラーモーダル表示可否 */
  showUploadErrorModal: boolean = false;

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.createView();
    return;
  }

  /**
   * ### 画面表示情報作成
   */
  public async createView(): Promise<void> {
    const followers: Array<FollowersGetModel> = await this.getFollowers();
    this.followerCont = followers.length;

    return;
  }

  /**
   * ### フォロワーを取得する
   */
  public async getFollowers(): Promise<Array<FollowersGetModel>> {
    const followers: Array<FollowersGetModel> = await this.followersService.gets(userStore);
    return followers;
  }

  /**
   * ### メッセージを送信する
   */
  public async sendMessage(messageModel: SendMessageModel): Promise<void> {
    // ファイルアップロードするタイミングでファイルが削除（移動含む）、リネームされた場合は失敗するのでここでtry-catchする
    // アップロードに失敗した場合は添付されたファイルを全てリセットしてモーダルで再添付を促す
    let files: Array<UploadFile> = [];
    try {
      files = await this.uploadFiles(messageModel);
    } catch (error) {
      this.showUploadErrorModal = true;
      this.sendMessagesTab.resetFiles();

      return;
    }

    // S3のアップロードは時間がかかるので他画面へ遷移されてしまうことがありえる
    // 画面遷移していたら処理は中断する
    if (this.$route.path != PcUrl.FollowerList) {
      return;
    }

    await this.sendMessagePost(messageModel, files);

    // 画面遷移していたら処理は中断する
    if (this.$route.path != PcUrl.FollowerList) {
      return;
    }

    this.isBusy = false;
    this.showSendMessageSuccessModal = true;

    return;
  }

  /**
   * ### ファイルをアップロードする
   */
  public async uploadFiles(messageModel: SendMessageModel): Promise<Array<UploadFile>> {
    let files: Array<UploadFile> = [];
    let fileUrls: string[] = [];

    try {
      if (0 < messageModel.files.length) {
        const urls: Array<S3LocationResponse> = await this.createUploadUrls(messageModel.files);

        // プログレス付きモーダルを表示する
        // 進捗はファイルのアプロード状況を反映させるようにする
        this.currentUploadCount = 0;
        this.uploadCount = messageModel.files.length;
        this.progressMessage = "ファイルアップロード中。\n完了するまでそのままお待ちください。\nメッセージ送信中に別画面に遷移しますと処理がキャンセルされます。";
        this.progressLoading = true;
        // アップロード
        for (let i = 0; i < urls.length; i++) {
          this.currentUploadCount = i;
          await this.s3UploadsService.uploadFile(urls[i], messageModel.files[i]);

          const preUrl: string = urls[i].url.endsWith("/") ? urls[i].url : `${urls[i].url}/`;
          const s3Url: string = `${preUrl}${urls[i].fields["key"]}`;
          files.push({
            url: s3Url,
            name: messageModel.files[i].name,
            type: messageModel.files[i].type
          });
          fileUrls.push(s3Url);
        }
        // アップロードしたファイルはサムネイル画像が作成されたり、movからmp4への変換が行われる
        // ファイルの存在確認をして変換されたか確認する
        await this.existFile(fileUrls);
      }
    } finally {
      this.progressLoading = false;
    }

    return files;
  }

  /**
   * ### ファイルアップロード先のURLを発行する
   */
  public async createUploadUrls(files: Array<File>): Promise<Array<S3LocationResponse>> {
    const requestBody: S3LocationRequest = this.createCreateUploadUrlRequestBody(files);
    const url: Array<S3LocationResponse> = await this.s3LocationsService.post(userStore, requestBody);
    return url;
  }

  /**
   * ### ファイルアップロード先のURL作成用のリクエストボディを作成する
   */
  public createCreateUploadUrlRequestBody(files: Array<File>): S3LocationRequest {
    let extensions: Array<string> = [];
    for (const file of files) {
      const _file: Array<string> = file.name.split("/");
      const _extensions: Array<string> = _file[_file.length - 1].split(".");
      extensions.push("." + _extensions[_extensions.length - 1]);
    }

    const params: S3LocationMessage = {
      userId: userStore.loginUser.userId
    };

    const requestBody: S3LocationRequest = {
      type: S3LocationType.FOLLOWER_MESSAGE,
      count: files.length,
      extensions: extensions,
      params: params,
      acl: "public-read"
    };

    return requestBody;
  }

  /**
   * ### ファイルの存在確認を行う
   */
  public async existFile(fileUrls: Array<string>): Promise<void> {
    // ファイルがアップロードされているかをチェックする
    const fileRequestBody: ExistS3FilesPostModel = { urls: fileUrls };
    await this.existS3FilesService.post(userStore, fileRequestBody);

    // サムネイルのファイルが存在しているかをチェックする
    let thumbnailUrls: Array<string> = [];
    for (const url of fileUrls) {
      // サムネイルのファイル名は<元ファイル名>_thumbnailになる
      const splitedUrl: string[] = url.split("/");
      const splitedFileName: string[] = splitedUrl[splitedUrl.length - 1].split(".");

      // mov、mp4以外の場合はサムネイルをメッセージ送信時に作成するのでスキップ
      if (splitedFileName[1] != "mov" && splitedFileName[1] != "mp4") {
        continue;
      }

      const thumbnailFileName: string = splitedFileName[0] + "_thumbnail.jpeg";
      let thunmbnailUrl: string = "";
      for (let i = 0; i < splitedUrl.length - 1; i++) {
        thunmbnailUrl += splitedUrl[i] + "/";
      }
      thunmbnailUrl += thumbnailFileName;
      thumbnailUrls.push(thunmbnailUrl);
    }

    if (0 < thumbnailUrls.length) {
      const thumbnailRequestBody: ExistS3FilesPostModel = { urls: thumbnailUrls };
      await this.existS3FilesService.post(userStore, thumbnailRequestBody);
    }

    return;
  }

  /**
   * ### フォロワーメッセージを送信する
   */
  public async sendMessagePost(messageModel: SendMessageModel, files: Array<UploadFile>): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "送信中...";

    const requestBody: FollowerMessagesPostModel = this.createSendMessageRequestBody(messageModel, files);
    await this.followerMessagesService.post(userStore, requestBody);

    this.isBusy = false;

    return;
  }

  /**
   * ### フォロワーメッセージ送信用のリクエストボディを作成する
   */
  public createSendMessageRequestBody(messageModel: SendMessageModel, files: Array<UploadFile>): FollowerMessagesPostModel {
    return {
      title: messageModel.title,
      text: messageModel.text,
      files: files
    };
  }

  /**
   * ### 各タブを更新する
   */
  public async onClose(): Promise<void> {
    let promiseList: Array<Promise<any>> = [];

    promiseList.push(this.sendMessagesTab.refresh());
    promiseList.push(this.sendMessageHistoryTab.refresh());

    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      this.sendMessageHistoryTab.setFilterSetting();
    }
    next();
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

#follower-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }
    .spinner {
      width: 16px;
      height: 16px;
      color: $loading;
    }
    .follower-count {
      font-weight: bold;
    }
  }
}
</style>
